/**
 * GenericImmersion — Reusable immersion wrapper for any body area
 * Takes a visual engine component, text sync data, and affirmations as props
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, SkipForward } from 'lucide-react';
import { binauralVolume, getStoredAudioMode } from '../../utils/modeAudio';

const API = process.env.REACT_APP_BACKEND_URL;

const TOTAL_DURATION = 420;
const DISSOLVE_START = 395;
const BREATH_INHALE = 4000;
const BREATH_HOLD = 2000;
const BREATH_EXHALE = 6000;
const BREATH_REST = 2000;
const TOTAL_BREATHS = 3;

const SUBLIMINAL_FALLBACK = ['calm', 'safe', 'ease', 'open', 'whole'];

const GenericImmersion = ({ VisualEngine, textSync, affirmations = [], title = 'Immersion', onComplete }) => {
  const [mode, setMode] = useState('waiting');
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [dissolveOpacity, setDissolveOpacity] = useState(0);
  const [breathCount, setBreathCount] = useState(0);
  const [breathLabel, setBreathLabel] = useState('');
  const [breathScale, setBreathScale] = useState(1);
  const breathTimerRef = useRef(null);
  const [overlayText, setOverlayText] = useState(null);
  const shownTextsRef = useRef(new Set());
  const [subliminalText, setSubliminalText] = useState('');
  const subliminalTimerRef = useRef(null);
  const subliminalIndexRef = useRef(0);
  const elapsedRef = useRef(0);
  const timerRef = useRef(null);
  const binauralRef = useRef(null);

  const formatMins = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const runBreathCycle = useCallback((count) => {
    if (count >= TOTAL_BREATHS) {
      setBreathLabel('');
      setBreathScale(1);
      setTimeout(() => setMode('immersion'), 1500);
      return;
    }
    setBreathLabel('Breathe in');
    setBreathScale(1.15);
    breathTimerRef.current = setTimeout(() => {
      setBreathLabel('Hold');
      breathTimerRef.current = setTimeout(() => {
        setBreathLabel('Slowly release');
        setBreathScale(1);
        breathTimerRef.current = setTimeout(() => {
          setBreathLabel('');
          breathTimerRef.current = setTimeout(() => {
            setBreathCount(count + 1);
            runBreathCycle(count + 1);
          }, BREATH_REST);
        }, BREATH_EXHALE);
      }, BREATH_HOLD);
    }, BREATH_INHALE);
  }, []);

  const handleTap = useCallback(() => {
    if (mode !== 'waiting') return;
    try {
      binauralRef.current = new Audio(`${API}/api/audio/binaural/theta_6hz_healing_20min.mp3`);
      binauralRef.current.loop = true;
      binauralRef.current.volume = 0;
      binauralRef.current.load();
    } catch (e) {}
    setMode('breathing');
    runBreathCycle(0);
  }, [mode, runBreathCycle]);

  useEffect(() => {
    if (mode !== 'immersion') return;

    // Start binaural beats with gentle fade in — respecting Clear/Soft/Deep
    const BINAURAL_TARGET = binauralVolume(getStoredAudioMode());
    if (binauralRef.current) {
      binauralRef.current.play().then(() => {
        let vol = 0;
        const fade = setInterval(() => {
          vol += 0.005;
          if (vol >= BINAURAL_TARGET || !binauralRef.current) {
            clearInterval(fade);
            if (binauralRef.current) binauralRef.current.volume = BINAURAL_TARGET;
          } else if (binauralRef.current) {
            binauralRef.current.volume = vol;
          }
        }, 100);
      }).catch(() => {});
    }

    const start = Date.now();
    timerRef.current = setInterval(() => {
      const s = (Date.now() - start) / 1000;
      elapsedRef.current = s;
      setElapsed(s);
      if (s >= DISSOLVE_START) {
        setDissolveOpacity(Math.min(1, (s - DISSOLVE_START) / (TOTAL_DURATION - DISSOLVE_START)));
        // Fade out binaural during dissolve
        if (binauralRef.current && binauralRef.current.volume > 0.002) {
          binauralRef.current.volume = Math.max(0, binauralRef.current.volume - 0.002);
        }
      }
      if (s >= TOTAL_DURATION) {
        clearInterval(timerRef.current);
        if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
        setMode('done');
        setTimeout(onComplete, 2000);
      }
    }, 200);
    setShowSkip(true);
    const subWords = affirmations.length > 0 ? affirmations : SUBLIMINAL_FALLBACK;
    subliminalTimerRef.current = setInterval(() => {
      const idx = subliminalIndexRef.current % subWords.length;
      setSubliminalText(subWords[idx]);
      subliminalIndexRef.current++;
      setTimeout(() => setSubliminalText(''), 3500);
    }, 5000);
    return () => { clearInterval(timerRef.current); clearInterval(subliminalTimerRef.current); };
  }, [mode, affirmations, onComplete]);

  useEffect(() => {
    if (mode !== 'immersion' || !textSync) return;
    const check = setInterval(() => {
      const s = elapsedRef.current;
      for (const entry of textSync) {
        if (s >= entry.t && s < entry.t + entry.dur / 1000 && !shownTextsRef.current.has(entry.t)) {
          shownTextsRef.current.add(entry.t);
          setOverlayText(entry.text);
          setTimeout(() => setOverlayText(null), entry.dur);
          break;
        }
      }
    }, 300);
    return () => clearInterval(check);
  }, [mode, textSync]);

  // Sync binaural mute state
  useEffect(() => {
    if (binauralRef.current) binauralRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    return () => {
      clearTimeout(breathTimerRef.current);
      clearInterval(timerRef.current);
      clearInterval(subliminalTimerRef.current);
      if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
    };
  }, []);

  const handleSkip = () => {
    clearInterval(timerRef.current);
    clearInterval(subliminalTimerRef.current);
    if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
    setMode('done');
    setTimeout(onComplete, 500);
  };

  return (
    <motion.div className="fixed inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 2 }} data-testid="generic-immersion">
      <div className="fixed inset-0" style={{ background: 'radial-gradient(ellipse at 50% 42%, rgba(20,14,8,1) 0%, rgba(8,5,3,1) 60%)' }} />

      {mode === 'immersion' && (
        <motion.div className="fixed inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 3 }}>
          <VisualEngine elapsed={elapsed} totalDuration={TOTAL_DURATION} isActive={true} />
        </motion.div>
      )}

      {dissolveOpacity > 0 && <motion.div className="fixed inset-0 pointer-events-none" style={{ background: 'rgba(10,7,4,1)' }} animate={{ opacity: dissolveOpacity }} />}

      <AnimatePresence>
        {mode === 'waiting' && (
          <motion.div className="fixed inset-0 flex flex-col items-center justify-center z-20 cursor-pointer" onClick={handleTap}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.5 }} data-testid="generic-tap-begin">
            <motion.h2 className="text-2xl text-white/50 mb-8" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>{title}</motion.h2>
            <motion.p className="text-white/35 text-base" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
              animate={{ opacity: [0.25, 0.5, 0.25] }} transition={{ duration: 4, repeat: Infinity }}>Enter</motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mode === 'breathing' && (
          <motion.div className="fixed inset-0 flex flex-col items-center justify-center z-20 pointer-events-none"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }}>
            <p className="text-white/20 text-xs mb-6 tracking-widest uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>Settle in</p>
            <motion.div className="rounded-full"
              style={{ width: 80, height: 80, border: '1px solid rgba(210,175,130,0.15)', background: 'radial-gradient(circle, rgba(210,175,130,0.06) 0%, transparent 60%)' }}
              animate={{ scale: breathScale }}
              transition={{ duration: breathScale > 1 ? BREATH_INHALE / 1000 : BREATH_EXHALE / 1000, ease: 'easeInOut' }} />
            {breathLabel && (
              <motion.p className="text-white/40 text-sm mt-6" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={breathLabel}>{breathLabel}</motion.p>
            )}
            <p className="text-white/15 text-xs mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>{breathCount + 1} of {TOTAL_BREATHS}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {mode === 'immersion' && (
        <div className="fixed inset-0 flex flex-col items-center justify-end pb-32 z-10 pointer-events-none">
          <AnimatePresence mode="wait">
            {overlayText && (
              <motion.p key={overlayText} className="text-white/40 text-lg text-center px-10"
                style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 1.5 }}>{overlayText}</motion.p>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {subliminalText && (
              <motion.p key={subliminalText} className="text-white text-xs mt-4 text-center"
                style={{ fontFamily: "'Manrope', sans-serif", opacity: 0.04 }}
                initial={{ opacity: 0 }} animate={{ opacity: 0.04 }} exit={{ opacity: 0 }} transition={{ duration: 2 }}>{subliminalText}</motion.p>
            )}
          </AnimatePresence>
        </div>
      )}

      {mode === 'immersion' && (
        <div className="fixed bottom-6 left-0 right-0 flex items-center justify-between px-6 z-20">
          <p className="text-white/15 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>{formatMins(elapsed)}</p>
          <div className="flex items-center gap-4">
            <button onClick={() => setMuted(!muted)} className="p-2 text-white/20" data-testid="generic-mute-btn">
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            {showSkip && <button onClick={handleSkip} className="p-2 text-white/20" data-testid="generic-skip-btn"><SkipForward size={16} /></button>}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default GenericImmersion;
